import React, { useState, useEffect, useRef } from 'react';
import Message from '../Message/Message';
import './Chatbot.css';
import { getGPT3Response } from '../../api/gpt3';
import { chatbotValidationSchema } from './ChatbotValidation';
import { useDarkMode } from '../../context/DarkModeContext';
import { scroller } from 'react-scroll';

const GREETING = "Hello! I'm Jose's assistant. Ask me anything about his background, skills, or projects. I can also navigate the site for you — just ask!";

const NAV_TRIGGERS = [
  { keywords: ['navigate to contact', 'take me to contact', 'show contact', 'go to contact', 'contact section'], section: 'contact' },
  { keywords: ['navigate to projects', 'take me to projects', 'show projects', 'go to projects', 'project section'], section: 'projects' },
  { keywords: ['navigate to blog', 'take me to blog', 'show blog', 'go to blog', 'blog section'], section: 'blog' },
  { keywords: ['navigate to about', 'take me to about', 'show about', 'go to about', 'about section'], section: 'about' },
  { keywords: ['navigate to home', 'take me home', 'go home', 'top of page', 'go to top', 'back to top'], section: 'hero' },
];

const detectNavIntent = (text) => {
  const lower = text.toLowerCase();
  for (const trigger of NAV_TRIGGERS) {
    if (trigger.keywords.some(kw => lower.includes(kw))) {
      return trigger.section;
    }
  }
  return null;
};

const SparkIcon = () => (
  <svg className="spark-icon" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13 2 C13.5 7 19 12.5 24 13 C19 13.5 13.5 19 13 24 C12.5 19 7 13.5 2 13 C7 12.5 12.5 7 13 2 Z" fill="#0071e3"/>
    <path d="M13 7 C13.3 10 16 12.7 19 13 C16 13.3 13.3 16 13 19 C12.7 16 10 13.3 7 13 C10 12.7 12.7 10 13 7 Z" fill="white" opacity="0.5" transform="rotate(45 13 13)"/>
  </svg>
);

const Chatbot = () => {
  const { darkMode } = useDarkMode();
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([{ sender: 'bot', text: GREETING }]);
  const [isTyping, setIsTyping] = useState(false);
  const [errors, setErrors] = useState({});
  const [isVisible, setIsVisible] = useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const [avatarState, setAvatarState] = useState('idle');

  const messagesEndRef = useRef(null);

  useEffect(() => {
    const currentHour = new Date().getHours();
    setIsOnline(currentHour < 1 || currentHour >= 6);
  }, []);

  useEffect(() => {
    if (!messagesEndRef.current || messages.length === 0) return;
    messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const navigateTo = (section) => {
    scroller.scrollTo(section, {
      smooth: true,
      duration: 600,
      offset: -100,
    });
  };

  const handleChange = (e) => setMessage(e.target.value);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isOnline) {
      setMessages(prev => [...prev, {
        sender: 'bot',
        text: 'Sorry, I am currently offline. Please try again later.',
      }]);
      return;
    }

    try {
      await chatbotValidationSchema.validate({ message });
    } catch (err) {
      setErrors({ message: err.message });
      return;
    }

    if (message.trim() === '') return;
    setErrors({});

    const timestamp = new Date().toLocaleTimeString();
    setMessages(prev => [...prev, { sender: 'user', text: message, timestamp }]);

    const navSection = detectNavIntent(message);
    setMessage('');
    setIsTyping(true);
    setAvatarState('waiting');

    const conversationHistory = messages.map(msg => ({
      role: msg.sender === 'user' ? 'user' : 'assistant',
      content: msg.text,
    }));

    try {
      const response = await getGPT3Response(message, conversationHistory);
      setMessages(prev => [...prev, {
        sender: 'bot',
        text: (response || "I'm sorry, I don't have a response for that.").replace("[DOWNLOAD_RESUME]", "").trim(),
        isResume: response && response.includes('[DOWNLOAD_RESUME]'),
      }]);
      if (navSection) {
        setTimeout(() => navigateTo(navSection), 800);
      }

      // Also detect navigation from bot response text
      if (response && !navSection) {
        const botNavMatch = [
          { phrase: 'Navigating to projects', section: 'projects' },
          { phrase: 'Navigating to about', section: 'about' },
          { phrase: 'Navigating to contact', section: 'contact' },
          { phrase: 'Navigating to blog', section: 'blog' },
          { phrase: 'Navigating to hero', section: 'hero' },
          { phrase: 'Navigating to home', section: 'hero' },
        ].find(n => response.includes(n.phrase));
        if (botNavMatch) {
          setTimeout(() => {
            navigateTo(botNavMatch.section);
          }, 800);
        }
      }

      // Handle resume download trigger

    } catch (err) {
      setAvatarState('idle');
      setMessages(prev => [...prev, {
        sender: 'bot',
        text: 'Sorry, something went wrong. Please try again.',
      }]);
    }

    setIsTyping(false);
    setAvatarState('idle');
  };

  const statusClass = isOnline ? 'online' : 'offline';

  return (
    <>
      {/* Floating avatar button */}
      <div
        className={`chatbot-avatar-container ${!isVisible ? 'visible' : ''}`}
        onClick={() => setIsVisible(true)}
      >
        <div className="chatbot-avatar-wrapper">
          <div className={`spark-avatar-bg state-${avatarState} ${!isOnline ? "offline" : ""}`}>
            <SparkIcon />
          </div>
        </div>
      </div>

      {/* Chat window */}
      <div
        className={`chatbot-container ${darkMode ? 'dark-mode' : ''}`}
        id="chatbot-container"
        style={{ display: isVisible ? 'flex' : 'none' }}
      >
        <div className="chatbot-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div className="chatbot-avatar-wrapper">
              <div className={`spark-avatar-bg sm state-${avatarState} ${!isOnline ? "offline" : ""}`}>
                <SparkIcon />
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', fontSize: '14px' }}>
              <span style={{ fontWeight: 600 }}>AskJose</span>
              {isTyping
                ? <span style={{ fontSize: '12px', color: 'var(--gray)' }}>typing...</span>
                : <span style={{ fontSize: '12px', color: isOnline ? '#34c759' : '#ff3b30' }}>
                    {isOnline ? 'Online' : 'Offline'}
                  </span>
              }
            </div>
          </div>
          <button className="minimize-btn" onClick={() => setIsVisible(false)}>_</button>
        </div>

        <div className="chatbot-messages-container">
          {messages.map((msg, index) => (
            <Message
              key={index}
              message={msg.text}
              sender={msg.sender}
              timestamp={msg.timestamp}
              isTyping={isTyping && index === messages.length - 1}
              isResume={msg.isResume}
            />
          ))}
          <div ref={messagesEndRef} style={{ float: 'left', clear: 'both' }} />
        </div>

        <form onSubmit={handleSubmit}>
          <div className="chatbot-input-container">
            <input
              type="text"
              value={message}
              onChange={handleChange}
              placeholder="Type your message..."
              id="chatbot-input"
            />
            <button type="submit" className="chatbot-send-btn">Send</button>
          </div>
          {errors.message && <div className="error">{errors.message}</div>}
        </form>
      </div>
    </>
  );
};

export default Chatbot;
